import React from 'react';
import {t} from 'ttag';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import {Close, EditNote} from '@mui/icons-material';
import {FieldValues, useForm, UseFormReturn} from 'react-hook-form';
import {IInvoice} from '../../../../store/apis/interfaces/invoice';
import {styles, InvoiceEditForm, invoiceEditValidationSchema, ConfirmationEditDialog} from '@invoice-system';
import {yupResolver} from '@hookform/resolvers/yup';
import {countries} from '@components/country-selector/CountrySelector';

type InvoiceSystemCreateProps = {
  data: IInvoice;
  onClose: () => void;
};

export const InvoiceEditDialog = ({data, onClose}: InvoiceSystemCreateProps) => {
  const [confirmDialog, setConfirmDialog] = React.useState<any>(null);

  const methods = useForm({
    defaultValues: {
      billingName: data?.details?.Paxname || '',
      endorsement: data?.sequence ? data?.endorsment : data?.details?.Endorsement || '',
      email: data?.email ?? '',
      address: data?.address || '',
      taxId: data?.taxId || '',
      postalCode: data?.postalCode || '',
      city: data?.city || '',
      country: data?.country? countries?.find((value) => value.code === data?.country) : {code: 'PT', label: 'Portugal'}
    },
    resolver: yupResolver(invoiceEditValidationSchema)
  });


  const handleFormSubmit = () => {
    methods.handleSubmit((formData) => {
      const countryCode = formData.country?.code || 'PT';
      setConfirmDialog({...formData, taxId: /^[A-Za-z]/.test(formData.taxId) ? formData.taxId : countryCode + formData.taxId, country: countryCode});
    })();
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth={'sm'}>
      <DialogTitle>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'start'}>
          <Box display={'flex'} flexDirection={'column'}>
            <Typography sx={styles.subtitle}>{t`Edit Invoice` + ` ${data?.docId || ''}`}</Typography>
            <Typography
              variant={'body2'}
              color={'text.secondary'}
            >{t`Modify the recipient details. Note that this will create a revised invoice.`}</Typography>
          </Box>
          <IconButton onClick={onClose} size={'small'}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <InvoiceEditForm {...(methods as unknown as UseFormReturn<FieldValues>)} />
      </DialogContent>
      <Divider />
      <DialogActions sx={styles.dialogActions}>
        <Button variant={'outlined'} sx={styles.dialogButton} onClick={onClose}>{t`Cancel`}</Button>
        <Button
          sx={styles.dialogButton}
          variant={'contained'}
          startIcon={<EditNote sx={{fontSize: '23px !important'}} />}
          onClick={() => handleFormSubmit()}
        >{t`Update Invoice`}</Button>
      </DialogActions>
      {confirmDialog && (
        <ConfirmationEditDialog data={data} formData={confirmDialog} onClose={() => setConfirmDialog(null)} />
      )}
    </Dialog>
  );
};
